import { Link, NavLink, Outlet, useLocation, useParams } from "react-router-dom";
import { ArrowUpRight, Sparkle } from "@phosphor-icons/react";
import { usePublicSite } from "@/context/PublicSiteContext";
import Seo from "@/components/Seo";
import { siteOgImage, siteUrl, jsonLdForOrg } from "@/lib/siteMeta";
import ShareButton from "@/components/marketing/ShareButton";
import ErrorBoundary from "@/components/ErrorBoundary";
import SkipToMain from "@/components/a11y/SkipToMain";
import HoneyAcornMark from "@/brand/HoneyAcornMark";

/**
 * Public marketing-site shell. Lives OUTSIDE the authenticated routes.
 * Inherits org branding by spreading `brandCss` onto the root element
 * so Tailwind's CSS variables downstream use the org's primary/accent.
 *
 * Navigation auto-generates from the list of published CMS pages, with
 * a few canonical entries (Home, Announcements, Contact) pinned by hand.
 */
export default function SiteLayout() {
  const { orgSlug } = useParams();
  const location = useLocation();
  const { loading, site, error, brandCss } = usePublicSite();

  if (loading) {
    return (
      <div
        data-testid="public-site-loading"
        className="flex min-h-screen items-center justify-center bg-[#FAF6EF]"
      >
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-[color:var(--color-primary,#5A7E46)] border-t-transparent" />
      </div>
    );
  }

  if (error || !site) {
    return (
      <div
        data-testid="public-site-missing"
        className="flex min-h-screen flex-col items-center justify-center bg-[#FAF6EF] px-6 text-center"
      >
        <Sparkle size={32} weight="duotone" className="text-neutral-400" />
        <h1 className="mt-4 font-heading text-2xl font-semibold text-neutral-900">
          We couldn't find that center
        </h1>
        <p className="mt-2 max-w-md text-sm text-neutral-600">
          The link may have changed, or the center hasn't published its public page yet.
          If you're a parent, please head to <Link className="font-semibold underline" to="/login">the portal</Link>.
        </p>
      </div>
    );
  }

  const { org, pages, announcements } = site;
  const base = `/site/${orgSlug}`;
  const siteName = org.display_name || org.name;

  const topPages = (pages || []).slice(0, 6);

  return (
    <div
      data-testid="public-site-root"
      data-org-slug={orgSlug}
      style={brandCss}
      className="min-h-screen bg-[#FAF6EF] text-neutral-900"
    >
      <SkipToMain targetId="main" />
      {/* Site-wide SEO defaults — individual pages override title/description. */}
      <Seo
        siteName={siteName}
        themeColor={org.brand_primary_hex || "#5A7E46"}
      />
      <header
        data-testid="public-site-header"
        role="banner"
        className="sticky top-0 z-40 border-b border-neutral-200 bg-white/90 backdrop-blur-xl"
      >
        <div
          aria-hidden
          className="h-1 w-full"
          style={{
            background: `linear-gradient(90deg, var(--color-primary), var(--color-accent))`,
          }}
        />
        <nav className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3 sm:px-5 sm:py-4" aria-label="Primary desktop">
          <Link
            to={base}
            data-testid="public-site-brand"
            className="flex items-center gap-3 transition hover:opacity-90"
          >
            {org.logo_url ? (
              <img
                src={org.logo_url}
                alt={org.display_name}
                width={36}
                height={36}
                loading="eager"
                decoding="async"
                className="h-9 w-9 rounded-xl border border-neutral-200 bg-white object-cover"
              />
            ) : (
              <span
                className="flex h-9 w-9 items-center justify-center rounded-xl text-sm font-bold text-white shadow-sm"
                style={{ background: "var(--color-primary)" }}
              >
                {(org.display_name || org.name).slice(0, 1).toUpperCase()}
              </span>
            )}
            <div className="leading-tight">
              <p
                className="text-[10px] font-bold uppercase tracking-[0.18em]"
                style={{ color: "var(--color-primary)" }}
              >
                Welcome
              </p>
              <p className="font-heading text-[15px] font-semibold text-neutral-900">
                {org.display_name || org.name}
              </p>
            </div>
          </Link>

          <div className="hidden items-center gap-1 md:flex">
            <NavItem to={base} end>Home</NavItem>
            {topPages.map((p) => (
              <NavItem key={p.id} to={`${base}/page/${p.slug}`}>
                {p.title}
              </NavItem>
            ))}
            {announcements?.length > 0 && (
              <NavItem to={`${base}/announcements`}>Announcements</NavItem>
            )}
            <NavItem to={`${base}/contact`}>Contact</NavItem>
          </div>

          <div className="flex items-center gap-2">
            <ShareButton
              variant="ghost"
              className="hidden sm:inline-flex"
              url={typeof window !== "undefined" ? window.location.origin + location.pathname : null}
              title={`${siteName} — ${document.title || siteName}`}
              text={`Check out ${siteName}`}
            />
            <Link
              to="/login"
              data-testid="public-site-portal-link"
              className="ml-0 inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 transition hover:border-neutral-300 hover:bg-neutral-50"
            >
              Parent portal
              <ArrowUpRight size={12} weight="bold" />
            </Link>
          </div>
        </nav>

        {/* Mobile nav strip */}
        <div
          aria-label="Primary mobile"
          role="navigation"
          className="overflow-x-auto border-t border-neutral-100 bg-white md:hidden"
        >
          <div className="flex min-w-max items-center gap-1 px-4 py-2">
            <NavItem to={base} end>Home</NavItem>
            {topPages.map((p) => (
              <NavItem key={p.id} to={`${base}/page/${p.slug}`}>
                {p.title}
              </NavItem>
            ))}
            {announcements?.length > 0 && (
              <NavItem to={`${base}/announcements`}>Announcements</NavItem>
            )}
            <NavItem to={`${base}/contact`}>Contact</NavItem>
          </div>
        </div>
      </header>

      <main
        id="main"
        data-testid="public-site-main"
        role="main"
        tabIndex={-1}
        className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-5 md:py-14"
      >
        <ErrorBoundary scope="public">
          <Outlet />
        </ErrorBoundary>
      </main>

      <footer data-testid="public-site-footer" role="contentinfo" className="border-t border-neutral-200 bg-white">
        <div className="mx-auto grid w-full max-w-6xl gap-6 px-5 py-10 md:grid-cols-3">
          <div>
            <p
              className="text-[10px] font-bold uppercase tracking-[0.18em]"
              style={{ color: "var(--color-primary)" }}
            >
              Get in touch
            </p>
            <p className="mt-1 font-heading text-lg font-semibold text-neutral-900">
              {org.display_name || org.name}
            </p>
          </div>
          <div className="space-y-1 text-sm text-neutral-600">
            {org.contact_address && <p>{org.contact_address}</p>}
            {org.contact_phone &&   <p>{org.contact_phone}</p>}
            {org.contact_email && (
              <p>
                <a className="underline hover:text-neutral-900" href={`mailto:${org.contact_email}`}>
                  {org.contact_email}
                </a>
              </p>
            )}
          </div>
          <div className="flex flex-col items-start gap-2 md:items-end">
            <Link
              to={`${base}/contact`}
              data-testid="public-footer-cta"
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold text-white transition"
              style={{ background: "var(--color-primary)" }}
            >
              Start an inquiry
              <ArrowUpRight size={12} weight="bold" />
            </Link>
            <p className="flex items-center gap-1.5 text-[11px] text-neutral-400">
              Powered by
              <HoneyAcornMark size={14} />
              <span className="font-heading font-semibold text-neutral-500">
                HoneyAcorn
              </span>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function NavItem({ to, end, children }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `inline-flex rounded-full px-3 py-1.5 text-sm font-medium transition ${
          isActive
            ? "bg-neutral-100 text-neutral-900"
            : "text-neutral-600 hover:text-neutral-900"
        }`
      }
    >
      {children}
    </NavLink>
  );
}
